import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai';
import Loader2 from '../../components/loader/Loader2';
import './style.css';
import { useLocation } from 'react-router-dom';
import { editUserByUserId, getUserByUserId } from '../../../services/Admin';

function EditUser() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const initialUserId = params.get('userId') || '';

  const [formData, setFormData] = useState({
    userId: initialUserId,
    name: '',
    email: '',
    phone: '',
    verified: 'false',
    blockStatus: 'false',
    isRewardBlock: 'false',
    walletAddress: '',
    password: ''
  });

  const [isPending, setIsPending] = useState(false);
  const [isUserValid, setIsUserValid] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const verifyUserId = async () => {
    if (!formData.userId) {
      toast.warn("Please enter User ID");
      return;
    }
    setIsPending(true);
    try {
      const response = await getUserByUserId(formData.userId.toUpperCase());
      
      const userData = response.data || response;
      
      if (userData && userData.userId) {
        setFormData({
          userId: userData.userId,
          name: userData.name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          verified: String(userData.verified ?? false),
          blockStatus: String(userData.blockStatus ?? false),
          isRewardBlock: String(userData.isRewardBlock ?? false),
          walletAddress: userData.walletAddress || '',
          password: '' // never prefilled — blank means "keep current password"
        });
        setPasswordVisible(false);
        setIsUserValid(true);
        toast.success("User found!");
      } else {
        setIsUserValid(false);
        toast.error("User not found or invalid response structure");
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      setIsUserValid(false);
      toast.error(error.response?.data?.message || "Failed to fetch user");
    }
    setIsPending(false);
  };

  useEffect(() => {
    if (initialUserId) {
      verifyUserId();
    }
  }, [initialUserId]);

  const handleUpdateUser = async () => {
    // Only the User ID is compulsory — every other field is optional.
    if (!formData.userId) {
      toast.warn("Please verify a User ID first!");
      return;
    }
    setIsPending(true);
    try {
      const payload = {
        userId: formData.userId,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        verified: formData.verified === 'true',
        blockStatus: formData.blockStatus === 'true',
        isRewardBlock: formData.isRewardBlock === 'true'
      };

      // Optional — only sent when the admin enters a value. Blank = unchanged.
      if (formData.walletAddress && formData.walletAddress.trim()) {
        payload.walletAddress = formData.walletAddress.trim();
      }
      if (formData.password && formData.password.trim()) {
        payload.password = formData.password;
      }

      const response = await editUserByUserId(payload);

      if (response && (response.success || response.data)) {
        toast.success("User updated successfully!");
        setIsUserValid(true);
        // Clear the password field after a successful update.
        setFormData(prev => ({ ...prev, password: '' }));
        setPasswordVisible(false);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to update user!");
    }
    setIsPending(false);
  };

  return (
    <div className="edit-user">
      <h1 className="main-heading">Edit User</h1>

      {isPending && <Loader2 />}

      <div className="adpa-first">
        <div className="adpafi-top">
          <h2>Enter User ID</h2>
        </div>
        <div className="adpafi-body">
          <div className="input-group">
            <label>User ID</label>
            <input
              type="text"
              name="userId"
              value={formData.userId}
              onChange={handleChange}
              placeholder="Enter UserId (ex: CGT6142)"
            />
          </div>
          <div className="button-group">
            <button type="button" onClick={verifyUserId}>
              Verify User
            </button>
          </div>
        </div>
      </div>

      {isUserValid && (
        <div className="adpa-first">
          <div className="adpafi-top">
            <h2>Editing User: {formData.name}</h2> {/* Changed from userName to formData.name */}
          </div>

          <div className="adpafi-body">
            <div className="input-group">
              <label>Full Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div className="input-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className="input-group">
              <label>Phone</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            <div className="input-group">
              <label>Wallet Address</label>
              <input
                type="text"
                name="walletAddress"
                value={formData.walletAddress}
                onChange={handleChange}
                placeholder="0x... (leave unchanged to keep current)"
              />
            </div>

            <div className="input-group">
              <label>New Password</label>
              <div className="password-field">
                <input
                  type={passwordVisible ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  placeholder="Leave blank to keep current password"
                />
                <span
                  className="eye"
                  onClick={() => setPasswordVisible((prev) => !prev)}
                >
                  {passwordVisible ? <AiFillEye /> : <AiFillEyeInvisible />}
                </span>
              </div>
            </div>

            <div className="input-group">
              <label>Verified</label>
              <select
                name="verified"
                value={formData.verified}
                onChange={handleChange}
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>

            <div className="input-group">
              <label>Block Status</label>
              <select
                name="blockStatus"
                value={formData.blockStatus}
                onChange={handleChange}
              >
                <option value="true">Blocked</option>
                <option value="false">Active</option>
              </select>
            </div>

            <div className="input-group">
              <label>Reward Block</label>
              <select
                name="isRewardBlock"
                value={formData.isRewardBlock}
                onChange={handleChange}
              >
                <option value="true">Blocked</option>
                <option value="false">Allowed</option>
              </select>
            </div>

            <div className="button-group">
              <button type="button" onClick={handleUpdateUser}>
                Update User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditUser;